import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface MetricCardProps {
  label: string;
  value: string;
  subValue?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  trendLabel?: string;
  variant?: 'default' | 'alert' | 'warning' | 'success' | 'info';
  icon?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

const VARIANT_STYLES = {
  default: 'bg-card border-border',
  alert: 'bg-danger/5 border-danger/20',
  warning: 'bg-warning/5 border-warning/20',
  success: 'bg-success/5 border-success/20',
  info: 'bg-info/5 border-info/20',
};

export default function MetricCard({
  label,
  value,
  subValue,
  trend,
  trendValue,
  trendLabel,
  variant = 'default',
  icon,
  className = '',
  children,
}: MetricCardProps) {
  return (
    <div className={`rounded-xl border p-5 ${VARIANT_STYLES[variant]} ${className}`}>
      <div className="flex items-start justify-between mb-3">
        <p className="text-[12px] font-500 uppercase tracking-[0.06em] text-muted-foreground">
          {label}
        </p>
        {icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            variant === 'alert' ? 'bg-danger/10 text-danger' :
            variant === 'warning' ? 'bg-warning/10 text-warning' :
            variant === 'success' ? 'bg-success/10 text-success' :
            variant === 'info'? 'bg-info/10 text-info' : 'bg-secondary text-muted-foreground'
          }`}>
            {icon}
          </div>
        )}
      </div>

      <p className={`font-700 tabular-nums font-mono leading-none mb-1 ${
        value.length > 8 ? 'text-2xl' : 'text-3xl'
      } ${
        variant === 'alert' ? 'text-danger' :
        variant === 'warning' ? 'text-warning' :
        variant === 'success' ? 'text-success' :
        variant === 'info'? 'text-info' : 'text-foreground'
      }`}>
        {value}
      </p>

      {subValue && (
        <p className="text-[12px] text-muted-foreground mb-2">{subValue}</p>
      )}

      {trend && trendValue && (
        <div className="flex items-center gap-1 mt-2">
          {trend === 'up' && <TrendingUp size={12} className="text-success" />}
          {trend === 'down' && <TrendingDown size={12} className="text-danger" />}
          {trend === 'neutral' && <Minus size={12} className="text-muted-foreground" />}
          <span className={`text-[11px] font-500 ${
            trend === 'up' ? 'text-success' : trend === 'down' ? 'text-danger' : 'text-muted-foreground'
          }`}>
            {trendValue}
          </span>
          {trendLabel && (
            <span className="text-[11px] text-muted-foreground">{trendLabel}</span>
          )}
        </div>
      )}

      {children}
    </div>
  );
}